import { CampaignGroupOptimisationTargets, PerformanceMode } from '../../../targets'
import { enums, resources, common, ReportOptions } from '../../../gads'
import { Improvement } from '../../improvement'
import { GadsFieldsRequired } from '../../../utils/global'

export namespace ImpTypes {
    // The core data of the improvement, available in the front-end
    export interface Body {
        campaigns: CampaignWithNetworkMetrics[]
        group: CampaignGroupOptimisationTargets
        performance: {
            group: {
                cpa?: number
                roas?: number
                profit?: number
                roi?: number
            }
            campaign: {
                cpa?: number
                roas?: number
                profit?: number
                roi?: number
            }
        }
        single: boolean
        lookback_window: ValidLookbackWindows
        performance_mode: PerformanceMode
    }

    // Error codes
    export const Error = {} as const

    export type Error = (typeof Error)[keyof typeof Error]

    // Human readable error message, used by support
    export const ErrorReadable = {}

    // Generate no improvement messages
    export const NoImprovementMessage = {
        NO_CAMPAIGNS: 'no_campaigns_in_last_90d',
        NO_IMPROVEMENTS: 'no_improvements',
        NO_SUITABLE_CAMPAIGN_ENVIRONMENTS: 'no_suitable_campaign_environments',
    }
}

// Data processing types, not explicitly related to core types
export type QueriesByLookbackWindow = {
    [window in ValidLookbackWindows]: ReportOptions
}

export interface CampaignEnvironment {
    campaigns: LookbackCampaign[]
    group: CampaignGroupOptimisationTargets
    min_clicks: MinimumClicks
}

export interface SearchPartnerCampaign {
    campaign: Campaign
    metrics: Metrics
    segments: Segments
}

export interface CampaignWithNetworkMetrics {
    campaign: Campaign
    metrics: NetworkMetrics
}

export interface NetworkMetrics {
    [enums.AdNetworkType.SEARCH]: Metrics
    [enums.AdNetworkType.SEARCH_PARTNERS]: Metrics | undefined
    total: Metrics
}

export interface LookbackCampaign {
    campaign: Campaign
    metrics: LookbackMetrics
}

type Campaign = GadsFieldsRequired<
    resources.Campaign,
    'id' | 'name' | 'resource_name' | 'advertising_channel_sub_type' | 'network_settings'
>

export type Metrics = GadsFieldsRequired<
    common.Metrics,
    'clicks' | 'cost_micros' | 'all_conversions' | 'all_conversions_value'
>

type Segments = GadsFieldsRequired<common.Segments, 'ad_network_type'>

export type LookbackMetrics = {
    [window in ValidLookbackWindows]: NetworkMetrics
}

export type MinimumClicks = {
    [window in ValidLookbackWindows]: NetworkMinClicks
}

export type NetworkMinClicks = {
    [enums.AdNetworkType.SEARCH]: number
    [enums.AdNetworkType.SEARCH_PARTNERS]: number
    total: number
}

export type ValidLookbackWindows =
    | Improvement.LookbackWindow.NINETY_DAYS
    | Improvement.LookbackWindow.ONE_HUNDRED_EIGHTY_DAYS
    | Improvement.LookbackWindow.ONE_YEAR

export type ValidNetworks = enums.AdNetworkType.SEARCH | enums.AdNetworkType.SEARCH_PARTNERS

export interface CampaignEnvGroup {
    campaigns: CampaignWithNetworkMetrics[]
    env: CampaignEnvironment
    window: ValidLookbackWindows
}

export type ValidCampaign = Omit<SearchPartnerCampaign, 'segments'>

export interface ImprovementGroups {
    campaigns_above_min_clicks: CampaignEnvGroup[]
    campaigns_below_min_clicks: CampaignEnvGroup[]
}
